import React, { useMemo, useState } from 'react';
import { RequireOrg, VpState } from '../../components/VelopulseShell';
import useLoad from '../../utils/useLoad';
import { vpApi, errorMessage } from '../../api/velopulse';

const todayIso = () => {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit',
  });
  return fmt.format(new Date());
};

function messageOf(err) {
  return err?.response?.data?.error || errorMessage(err, '號牌查詢失敗，請稍後再試。');
}

function PlateLookup() {
  const { data: meta, error: metaError, loading } = useLoad(() => vpApi.getPlateMeta(), []);
  const [city, setCity] = useState('臺北市');
  const [stationId, setStationId] = useState('');
  const [vehicleType, setVehicleType] = useState('car_private');
  const [queryDate, setQueryDate] = useState(todayIso);
  const [period, setPeriod] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const stations = useMemo(() => {
    const all = meta?.stations || [];
    return city ? all.filter((s) => s.cities.includes(city)) : all;
  }, [meta, city]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setResult(null);
    try {
      const data = await vpApi.lookupPlate({
        city,
        station_id: stationId || undefined,
        vehicle_type: vehicleType,
        query_date: queryDate,
        period: period || undefined,
      });
      setResult(data);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="vp-content"><VpState text="正在讀取監理站名錄…" /></div>;
  if (metaError) {
    return (
      <div className="vp-content">
        <div className="vp-error" role="alert">{messageOf(metaError)}</div>
      </div>
    );
  }

  return (
    <div className="vp-content vp-tax">
      <div className="vp-hello">
        <div>
          <h1>監理站號牌查詢</h1>
          <p>查縣市或監理站、車輛種類在指定日期的號牌資訊。沒有官方即時 API 時只顯示示範資料或無法查詢，不會改去抓監理服務網。</p>
        </div>
      </div>
      <div className="vp-tax-layout">
        <form className="vp-card" onSubmit={submit}>
          <h2>查詢條件</h2>
          <div className="vp-field">
            <label htmlFor="plate-city">縣市</label>
            <select id="plate-city" className="vp-input" value={city} onChange={(e) => { setCity(e.target.value); setStationId(''); }}>
              {(meta.cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="vp-field">
            <label htmlFor="plate-station">監理站</label>
            <select id="plate-station" className="vp-input" value={stationId} onChange={(e) => setStationId(e.target.value)}>
              <option value="">依縣市帶出預設站</option>
              {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="vp-field">
            <label htmlFor="plate-type">車輛種類</label>
            <select id="plate-type" className="vp-input" value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}>
              {(meta.vehicle_types || []).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div className="vp-row2">
            <div className="vp-field">
              <label htmlFor="plate-date">查詢日</label>
              <input id="plate-date" className="vp-input" type="date" required value={queryDate} onChange={(e) => setQueryDate(e.target.value)} />
            </div>
            <div className="vp-field">
              <label htmlFor="plate-period">期別（可空白）</label>
              <input id="plate-period" className="vp-input" placeholder="YYYY-MM" value={period} onChange={(e) => setPeriod(e.target.value)} />
            </div>
          </div>
          {error && <div className="vp-error" role="alert">{error}</div>}
          <div className="vp-wiz-actions">
            <button type="submit" className="vp-btn lg primary" disabled={saving}>{saving ? '查詢中…' : '查詢號牌'}</button>
          </div>
          <p className="vp-hint">{meta.directory_note}</p>
        </form>

        <div className="vp-card">
          <h2>查詢結果</h2>
          {!result && <VpState text="選好條件後按「查詢號牌」。結果會標明是示範資料，或是目前無法向監理站取得即時號碼。" />}
          {result && (
            <div>
              <div className={result.is_mock || result.degraded ? 'vp-warnbox' : 'vp-okbox'} role="status">
                {result.is_mock ? '示範資料，不是監理站今日可領號牌。' : (result.message || '查詢完成。')}
              </div>
              <div className="vp-tax-kpis">
                <div><div className="l">監理站</div><div className="v">{result.query?.station_name}</div></div>
                <div><div className="l">車輛種類</div><div className="v">{result.query?.vehicle_type_label}</div></div>
                <div><div className="l">查詢日</div><div className="v">{result.query?.query_date}</div></div>
                <div><div className="l">狀態</div><div className="v">{result.status}</div></div>
              </div>
              {!!result.plates?.length && (
                <ul className="vp-plate-list">
                  {result.plates.map((p) => (
                    <li key={p.number}>
                      <span className="vp-plate-no">{p.number}</span>
                      <span className="vp-muted">{p.kind}{p.note ? `・${p.note}` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
              {!result.plates?.length && <p className="vp-hint">{result.message}</p>}
              <p className="vp-disclaimer" role="note">{result.disclaimer}</p>
              {!!result.sources?.length && (
                <p className="vp-hint">
                  來源：{result.sources.map((s, i) => (
                    <React.Fragment key={s.url}>
                      {i > 0 ? '、' : ''}
                      <a href={s.url} target="_blank" rel="noopener noreferrer">{s.title}</a>
                    </React.Fragment>
                  ))}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PlateLookupPage() {
  return <RequireOrg><PlateLookup /></RequireOrg>;
}
